import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";

import { CategoryPageSkeleton } from "../../../component/skeleton/CategoryPageSkeleton";

import { StatusPill } from "../../order/orderTable/orderTableColumnsConfig";

import classes from "./refundDetailPage.module.css";

import { useGetRefundByIdQuery } from "../../../services/refund/refundApiSlice";
import { usePatchRefundStatusMutation } from "../../../services/refund/refundMutationApiSlice";
import { RefundDetailForm } from "../../../component/refund/refundDetail/RefundDetailForm";

const refundConfig = {
  "refund requested": {
    label: "Refund Requested",
    bg: "#FFF4E5",
    color: "#FF9500",
    Icon: ClockIcon,
  },
  "refund in process": {
    label: "Refund In Process",
    bg: "#E6F0FF",
    color: "#1E56D9",
    Icon: ClockIcon,
  },
  "refund rejected": {
    label: "Refund Rejected",
    bg: "#FDECEA",
    color: "#D92D20",
    Icon: XCircleIcon,
  },
  refunded: {
    label: "Refunded",
    bg: "#E6F7EF",
    color: "#00A167",
    Icon: CheckCircleIcon,
  },
  unknown: {
    label: "Unknown",
    bg: "#FFF4E5", // light amber/orange background for caution
    color: "#FF9500", // amber/orange text to indicate attention
    Icon: ClockIcon, // clock icon to indicate pending/unknown state
  },
};

export const RefundDetailPage = () => {
  const { refundId } = useParams();
  const navigate = useNavigate();

  const { data: refundDetail, isSuccess } = useGetRefundByIdQuery(refundId);

  const [updateRefund, { isLoading: isUpdating }] =
    usePatchRefundStatusMutation();

  // current status → UI config
  const refundStatusKey = refundDetail?.status?.toLowerCase() ?? "";
  const refundStatusConfig = refundConfig[refundStatusKey];

  const handleUpdate = async (payload) => {
    try {
      const response = await updateRefund({
        id: refundId, // <-- mutation expects `id`
        status: Number(payload.order_status), // <-- map to `status`
        remarks: payload?.remarks || undefined, // <-- optional
      }).unwrap();

      toast.success(
        response?.message?.displayMessage || "Refund updated successfully"
      );

      navigate("/refunds");
    } catch (err) {
      toast.error(
        err?.data?.message?.displayMessage ||
          err?.error ||
          "Failed to update refund"
      );
    }
  };

  return isSuccess ? (
    <div className={classes.wrapper}>
      <div className={classes.page}>
        <h2
          className={classes.pageTitle}
        >{`Refund ID: ${refundDetail?.id}`}</h2>

        <div className={classes.page__payment}>
          <h2 className={classes.page__payment__title}>Refund Status:</h2>

          {refundStatusConfig && <StatusPill {...refundStatusConfig} />}
        </div>
      </div>

      <RefundDetailForm
        initialValues={refundDetail}
        onSubmit={handleUpdate}
        isSubmitting={isUpdating}
      />
    </div>
  ) : (
    <CategoryPageSkeleton />
  );
};
